import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Upload, FileText, Check, Mail } from "lucide-react";
import { useLanguage } from "../i18n";

// Animated walkthrough of the real request form (/richiedi-concept): same sections, same labels (taken from the
// translations the form itself uses), up to the confirmation the customer actually sees. Keep it in step with ConceptForm.
const SCENE_MS = 4200;

const Window = ({ title, children }) => (
  <div className="absolute inset-4 sm:inset-6 bg-cream rounded-sm shadow-[0_20px_50px_rgba(0,0,0,0.35)] overflow-hidden flex flex-col">
    <div className="h-7 bg-sand border-b border-ink/10 flex items-center gap-1.5 px-3 shrink-0">
      <span className="w-2 h-2 rounded-full bg-ink/20" /><span className="w-2 h-2 rounded-full bg-ink/20" /><span className="w-2 h-2 rounded-full bg-ink/20" />
      <span className="ml-3 text-[9px] uppercase tracking-[0.2em] text-inksoft truncate">{title}</span>
    </div>
    <div className="flex-1 p-3 sm:p-4 relative overflow-hidden">{children}</div>
  </div>
);

const Cursor = ({ x, y, delay = 0.4 }) => (
  <motion.div
    className="absolute w-3 h-3 rounded-full bg-ink/80 ring-4 ring-amberl/40 pointer-events-none z-10"
    initial={{ left: "70%", top: "80%", opacity: 0 }}
    animate={{ left: x, top: y, opacity: 1, scale: [1, 0.7, 1] }}
    transition={{ duration: 0.9, delay, times: [0, 0.85, 1] }}
  />
);

const Lbl = ({ children }) => <p className="text-[7.5px] sm:text-[8px] uppercase tracking-[0.15em] text-inksoft truncate">{children}</p>;

const Field = ({ label, value, delay }) => (
  <div className="min-w-0">
    <Lbl>{label}</Lbl>
    <div className="h-6 bg-white border border-ink/15 rounded-sm px-2 flex items-center overflow-hidden">
      <motion.span className="text-[10px] text-ink whitespace-nowrap" initial={{ width: 0 }} animate={{ width: "100%" }} transition={{ delay, duration: 0.7, ease: "linear" }} style={{ overflow: "hidden", display: "block" }}>
        {value}
      </motion.span>
    </div>
  </div>
);

const Pick = ({ children, on, delay = 0 }) => (
  <motion.span className={`inline-flex items-center gap-1 text-[9px] px-2 h-6 rounded-sm border ${on ? "border-amberdark bg-amberl/15 text-ink" : "border-ink/15 bg-white text-inksoft"}`}
    initial={{ opacity: 0.6 }} animate={{ opacity: 1, ...(on ? { borderColor: ["rgba(42,39,38,0.15)", "rgba(184,135,90,1)"] } : {}) }} transition={{ delay, duration: 0.4 }}>
    {on && <motion.span initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: delay + 0.2 }}><Check className="w-2.5 h-2.5 text-amberdark" /></motion.span>}
    {children}
  </motion.span>
);

const SceneService = ({ t, kind }) => (
  <Window title={t.tutorial.kinds[kind]}>
    <Lbl>{t.twin.label}</Lbl>
    <div className="mt-2 grid grid-cols-2 gap-2">
      {["concept", "executive"].map((k, i) => (
        <motion.div key={k} className={`rounded-sm border p-2.5 ${k === kind ? "border-amberdark bg-amberl/10" : "border-ink/15 bg-white"}`}
          animate={k === kind ? { borderColor: ["rgba(42,39,38,0.15)", "rgba(184,135,90,1)"] } : {}} transition={{ delay: 1.2, duration: 0.4 }}>
          <p className="text-[10px] font-semibold text-ink flex items-center justify-between">{t.tutorial.kinds[k]}{k === kind && <motion.span initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 1.4 }}><Check className="w-3 h-3 text-amberdark" /></motion.span>}</p>
          <p className="mt-1 text-[8px] leading-snug text-inksoft line-clamp-3">{k === "concept" ? t.twin.baseDesc : t.twin.advancedDesc}</p>
        </motion.div>
      ))}
    </div>
    <Cursor x={kind === "concept" ? "28%" : "74%"} y="40%" delay={0.5} />
  </Window>
);

const SceneProfile = ({ t }) => (
  <Window title={t.profile.title}>
    <div className="grid grid-cols-2 gap-2.5">
      <Field label={t.profile.firstName} value="Giulia" delay={0.3} />
      <Field label={t.profile.lastName} value="Rossi" delay={0.8} />
      <Field label={t.profile.email} value="giulia.rossi@email.it" delay={1.3} />
      <Field label={t.profile.userType} value={t.profile.types.privato} delay={1.8} />
    </div>
    <Cursor x="30%" y="30%" delay={0.3} />
  </Window>
);

const SceneSpace = ({ t }) => (
  <Window title={t.review.space}>
    <div className="grid grid-cols-2 gap-2">
      <Field label={t.form.spaceType} value={t.pkg.spaceTypes.residenziale || "Residenziale"} delay={0.2} />
      <Field label={t.scope.label} value={t.scope.intero} delay={0.6} />
      <Field label={t.form.surface} value="95 mq" delay={1.0} />
      <Field label={t.form.ceiling} value="2,90 m" delay={1.4} />
      <div className="col-span-2">
        <Lbl>{t.budget.label}</Lbl>
        <div className="grid grid-cols-2 gap-2">
          <Field label={t.budget.from} value="3.000" delay={1.9} />
          <Field label={t.budget.to} value="6.000" delay={2.3} />
        </div>
      </div>
    </div>
    <Cursor x="35%" y="78%" delay={1.7} />
  </Window>
);

const SceneMood = ({ t }) => {
  const moods = Object.entries(t.pkg.moods || {}).slice(0, 4);
  return (
    <Window title={t.pkg.mood}>
      <Lbl>{t.pkg.mood}</Lbl>
      <div className="mt-1.5 flex flex-wrap gap-1.5">
        {moods.map(([k, label], i) => <Pick key={k} on={i === 1 || i === 2} delay={0.5 + i * 0.3}>{label}</Pick>)}
      </div>
      <div className="mt-3"><Lbl>{t.pkg.cct}</Lbl></div>
      <div className="mt-1.5 flex gap-1.5">{["2700", "3000", "4000"].map((k) => <Pick key={k} on={k === "3000"} delay={1.6}>{k} K</Pick>)}</div>
      <div className="mt-3"><Lbl>{t.effects.short}</Lbl></div>
      <div className="mt-1.5"><Pick on delay={2.3}>{t.effects.farlight}</Pick></div>
      <Cursor x="40%" y="82%" delay={2.0} />
    </Window>
  );
};

const SceneUpload = ({ t }) => (
  <Window title={t.twin.planUpload}>
    <motion.div className="h-[62%] border-2 border-dashed border-amberdark/60 rounded-sm flex flex-col items-center justify-center gap-2 bg-white"
      animate={{ borderColor: ["rgba(184,135,90,0.6)", "rgba(184,135,90,1)", "rgba(184,135,90,0.6)"] }} transition={{ duration: 1.6, repeat: Infinity }}>
      <Upload className="w-5 h-5 text-amberdark" strokeWidth={1.5} />
      <p className="text-[9px] uppercase tracking-[0.15em] font-semibold text-ink">{t.form.uploadButton}</p>
      <div className="flex gap-2">
        {["pianta.pdf", "layout.png"].map((f, i) => (
          <motion.span key={f} className="flex items-center gap-1 text-[9px] bg-sand border border-ink/10 rounded-sm px-2 py-1 text-ink"
            initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.0 + i * 0.45 }}>
            <FileText className="w-3 h-3 text-amberdark" />{f}<Check className="w-3 h-3 text-sagedark" />
          </motion.span>
        ))}
      </div>
    </motion.div>
    <motion.div className="mt-2.5 flex items-start gap-2 bg-white border border-ink/10 rounded-sm p-2" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2.0 }}>
      <motion.span className="mt-px w-3 h-3 shrink-0 rounded-[2px] border border-amberdark flex items-center justify-center" animate={{ backgroundColor: ["#ffffff", "#B8875A"] }} transition={{ delay: 2.8, duration: 0.2 }}>
        <Check className="w-2.5 h-2.5 text-white" />
      </motion.span>
      <span className="text-[8.5px] leading-snug text-inksoft">{t.consent.text} {t.legal.terms}, {t.legal.privacy}</span>
    </motion.div>
    <Cursor x="8%" y="84%" delay={2.4} />
  </Window>
);

// Concept: the summary pop-up with the confirmation checkbox, then payment. Executive: free quote request.
const SceneConfirm = ({ t, kind }) => (kind === "executive" ? (
  <Window title={t.tutorial.kinds.executive}>
    <div className="h-full flex flex-col justify-center gap-3">
      <motion.span className="mx-auto inline-flex items-center justify-center bg-ink text-cream rounded-sm px-5 h-8 text-[9px] font-semibold uppercase tracking-[0.15em]"
        animate={{ scale: [1, 0.96, 1] }} transition={{ delay: 0.9, duration: 0.3 }}>{t.services.requestQuote}</motion.span>
      <motion.p className="bg-[#F7F5F0] border border-[#C5A059] rounded-sm p-2.5 text-[9.5px] leading-snug text-ink" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.5 }}>
        {t.services.confirm}
      </motion.p>
    </div>
    <Cursor x="50%" y="38%" delay={0.3} />
  </Window>
) : (
  <Window title={t.review.title}>
    <div className="absolute inset-0 bg-ink/25" />
    <motion.div className="relative mx-auto max-w-[92%] bg-cream border border-ink/10 rounded-sm p-3 shadow-xl" initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.35 }}>
      <p className="font-display text-[13px] text-ink">{t.review.title}</p>
      <div className="mt-1.5 bg-white border border-ink/10 rounded-sm p-2 text-[8.5px] text-inksoft space-y-0.5">
        <p><span className="text-amberdark uppercase tracking-[0.12em]">{t.review.space}</span></p>
        <p>{t.form.spaceType}: <span className="text-ink">{t.pkg.spaceTypes.residenziale || "Residenziale"}</span> · {t.form.surface}: <span className="text-ink">95 mq</span></p>
        <p>{t.budget.short}: <span className="text-ink">3.000 – 6.000 €</span></p>
      </div>
      <div className="mt-1.5 flex items-start gap-2 bg-white border border-ink/10 rounded-sm p-2">
        <motion.span className="mt-px w-3 h-3 shrink-0 rounded-[2px] border border-amberdark flex items-center justify-center" animate={{ backgroundColor: ["#ffffff", "#B8875A"] }} transition={{ delay: 1.4, duration: 0.2 }}>
          <Check className="w-2.5 h-2.5 text-white" />
        </motion.span>
        <span className="text-[8.5px] leading-snug text-ink">{t.review.confirmText}</span>
      </div>
      <motion.div className="mt-1.5 h-7 rounded-sm bg-ink text-cream flex items-center justify-center text-[8.5px] font-semibold uppercase tracking-[0.12em]"
        initial={{ opacity: 0.45 }} animate={{ opacity: 1, scale: [1, 1, 0.97, 1] }} transition={{ delay: 1.7, duration: 1.2 }}>
        {t.review.pay} — {t.pricing.plans.base.price}
      </motion.div>
    </motion.div>
    <Cursor x="12%" y="66%" delay={0.9} />
  </Window>
));

const SceneDelivered = ({ t, kind }) => {
  const step = kind === "executive" ? t.tutorial.step4Executive : t.tutorial.steps[3];
  return (
    <Window title={kind === "executive" ? t.tutorial.kinds.executive : t.twin.received}>
      <div className="h-full flex flex-col justify-center gap-2">
        <motion.span className="mx-auto w-9 h-9 rounded-full bg-amberl/20 flex items-center justify-center" initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.3 }}>
          <Mail className="w-4 h-4 text-amberdark" />
        </motion.span>
        {kind !== "executive" && <p className="text-center text-[10px] font-semibold text-ink">{t.twin.received}</p>}
        <motion.p className="text-center font-display text-[13px] text-ink" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8 }}>{step.title}</motion.p>
        <motion.p className="text-center text-[9px] leading-snug text-inksoft px-2" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.3 }}>{step.desc}</motion.p>
      </div>
    </Window>
  );
};

export const SCENES = [SceneService, SceneProfile, SceneSpace, SceneMood, SceneUpload, SceneConfirm, SceneDelivered];

export const GuidedTutorial = ({ playing, onStep, kind = "concept" }) => {
  const { t } = useLanguage();
  const [step, setStep] = useState(0);
  useEffect(() => {
    if (!playing) return;
    const timer = setTimeout(() => setStep((s) => (s + 1) % SCENES.length), SCENE_MS);
    return () => clearTimeout(timer);
  }, [step, playing]);
  useEffect(() => { onStep?.(step); }, [step]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setStep(0); }, [kind]);
  const Scene = SCENES[step];
  return (
    <div data-testid="guided-tutorial" className="absolute inset-0 bg-espresso">
      <AnimatePresence mode="wait">
        <motion.div key={`${kind}-${step}`} className="absolute inset-0" initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.45 }}>
          <Scene t={t} kind={kind} />
        </motion.div>
      </AnimatePresence>
      <div className="absolute bottom-0 inset-x-0 h-1 bg-cream/15">
        <motion.div key={`bar-${kind}-${step}-${playing}`} className="h-full bg-amberl" initial={{ width: "0%" }} animate={{ width: playing ? "100%" : "0%" }} transition={{ duration: SCENE_MS / 1000, ease: "linear" }} />
      </div>
      <div className="absolute top-2 right-3 flex gap-1">
        {SCENES.map((_, i) => (
          <button key={i} data-testid={`tutorial-scene-dot-${i}`} onClick={() => setStep(i)} aria-label={`Step ${i + 1}`} className={`h-1 rounded-full transition-all ${i === step ? "w-5 bg-amberl" : "w-2 bg-cream/40"}`} />
        ))}
      </div>
    </div>
  );
};
